<template>
  <div>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div class="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
      <div>
        <h3 class="text-lg leading-6 font-medium text-gray-900">
          Entry Questions
        </h3>
          <p class="mt-2 max-w-2xl text-sm text-gray-600">
            Gather additional information from exhibitors. The exhibitor names and contact info (address, phone number, and email) is collected when they sigup to enter a show.
            <a class="link" href="https://showman.helpscoutdocs.com/article/5-entry-question-overview" target="_blank">Learn more about entry questions.</a>
          </p>
      </div>
      <div class="mt-3 flex sm:mt-0 sm:ml-4 space-x-2">
        <button @click="newEntryQuestionClicked" type="button" class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          + Add a question
        </button>
        <button @click="$refs.preview.show()" type="button" class="inline-flex items-center px-4 py-2 border border-blue-600 rounded-md shadow-sm text-sm font-medium text-blue-600 hover:text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          Preview entry questions
        </button>
        <button @click="saveOrder" class='inline-flex items-center px-4 py-2 font-medium link'>Save Order</button>
      </div>
    </div>
    <div class="flex flex-col mt-8">
      <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div class="overflow-hidden border border-gray-200 sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-200 ">
              <thead class="bg-gray-100">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Question
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Answer Type
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Due Date
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Pricing
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Details
                  </th>
                  <th scope="col" class="relative px-6 py-3">
                    <span class="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <draggable tag="tbody" v-if="allEntryQuestions.length > 0" v-model="event.entry_questions" group="market" ghost-class="ghost" handle=".handle" class="bg-white divide-y divide-gray-200">
                <tr v-for="(s, i) in allEntryQuestions" :key="i" class="w-full">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div class="flex items-center">
                      <span v-tooltip="'Drag to reorder'" class="text-xl handle draggable">&#9776;</span>
                      <div class="ml-4">
                        <p class="font-semibold">{{s.details.label}}</p>
                        <p>{{s.question}}</p>
                        <div class="flex flex-col md:hidden mt-2 text-xs">
                          <p>Answer Type: {{s.details.answer_type}}</p>
                          <p>Due Date: {{formatDate(s.details.due_date)}}</p>
                          <template v-if="s.is_pricing">
                            <p>Rate: {{currencySymbol}}{{s.pricing_details.rate}}</p>
                            <p v-if="event.entry_settings.has_member_pricing">Member Rate: {{currencySymbol}}{{s.pricing_details.member_rate}}</p>
                          </template>
                          <p v-if="s.details.exhibitor_question">EXHIBITOR QUESTION</p>
                          <p v-if="s.details.required">REQUIRED</p>
                          <p v-if="s.details.admin_only" >ADMIN ONLY</p>
                          <p v-if="s.details.multi_answer">MULTIPLE ANSWERS</p>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden md:table-cell">
                    {{s.details.answer_type}}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden md:table-cell">
                    {{formatDate(s.details.due_date)}}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden md:table-cell">
                    <template v-if="s.is_pricing">
                      <p>{{currencySymbol}}{{s.pricing_details.rate}}</p>
                      <p v-if="event.entry_settings.has_member_pricing">Members: {{currencySymbol}}{{s.pricing_details.member_rate}}</p>
                    </template>
                    <template v-else>
                      Non pricing question
                    </template>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-xs text-gray-600 font-bold space-y-1 hidden md:table-cell">
                    <p v-if="s.details.exhibitor_question">EXHIBITOR QUESTION</p>
                    <p v-if="s.details.required">REQUIRED</p>
                    <p v-if="s.details.admin_only" >ADMIN ONLY</p>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm  space-x-3">
                    <button class="text-blue-500 hover:text-blue-700" @click="editEntryQuestionClicked(s)">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button v-if="event.status !== 'PUBLISHED'" class="text-red-500 hover:text-red-700" @click="removeEntryQuestion(s)">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              </draggable>
              <!-- More people... -->
            </table>
          </div>
        </div>
      </div>
    </div>

    <b-modal ref="entryQuestion" title="Entry Question">
      <div v-if="isSupportUser && isPublished" class="relative flex items-center">
        <div class="flex items-center h-5">
          <input v-model="isUnlocked" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
        </div>
        <div class="ml-3 text-sm">
          <label for="comments" class="font-medium text-gray-700">Unlock Question</label>
        </div>
      </div>
      <div class="flex justify-start py-4 px-1 sm:px-2 items-center">
        <form @submit.prevent class="text-sm md:text-base">
          <div class="grid grid-cols-4 gap-5">

            <div class="col-span-4 sm:col-span-2">
              <label class="form-label">Label</label>
              <input class="new-form-input mt-1 w-full" v-model="entryQuestion.details.label" placeholder="Label">
              <p class="mt-1 text-sm text-gray-700" id="label-description">**LIMITED TO 25 CHARACTERS** This is a short label for each question, every label should be unique. Used for reporting and displaying to exhibitors.</p>
            </div>
            <div class="col-span-4 sm:col-span-2">
              <label class="form-label">Due Date</label>
              <date-picker v-model="entryQuestion.details.due_date" value-type="YYYY-MM-DD" :format="Localized.getDateFormat(event.location_details.country)" class="mt-1"></date-picker>
              <p class="mt-1 text-sm text-gray-700" id="due-date-description">What is the last this question can be answered? After this date is passed the exhibitors will not be able to answer/edit.</p>
            </div>
            <div class="col-span-4">
              <label class="form-label">Question</label>
              <input class="new-form-input mt-1 w-full" v-model="entryQuestion.question" placeholder="Enter a question">
              <p class="mt-1 text-sm text-gray-700" id="question-description">This is the question that will appear for the exhibitors to answer.</p>
            </div>

            <div class="col-span-4 sm:col-span-2">
              <label class="form-label">Details Text</label>
              <textarea class="new-form-input mt-1 w-full" v-model="entryQuestion.details.details" placeholder="Details"></textarea>
              <p class="mt-1 text-sm text-gray-700" id="question-description">Add in additional details about this question. This text will appear below the answer/input box.</p>
            </div>
            <div class="col-span-4 sm:col-span-2">
              <label class="form-label">Answer Type</label>
              <!--<multiselect class="w-full md:w-32" v-model="s.details.answer_type" :options="['number','text','checkbox','dropdown']" :searchable="false" :close-on-select="true" :show-labels="false" placeholder="Pick a value"></multiselect>-->
              <template v-if="!isLocked">
                <v-select
                  class="bg-white rounded mt-1"
                  v-model="entryQuestion.details.answer_type"
                  :options="answerTypeOptions"
                  label="value"
                  :reduce="option => option.value"
                  :clearable="false"
                >
                  <template #option="{ value, description }">
                    <p class="font-medium md:text-lg">{{ value.toUpperCase() }}</p>
                    <p class='text-xs md:text-sm whitespace-pre-wrap pb-2'>{{description}}</p>
                  </template>
                </v-select>
                <p class="mt-1 text-sm text-gray-700" id="answer-type-description">This determines the type of answer box the exhibitors will see for this question.</p>
              </template>
              <template v-else>
                <input class="px-3 py-2 border rounded-lg mt-1 w-full bg-gray-300 cursor-not-allowed" v-model="entryQuestion.details.answer_type" disabled>
                <p class="mt-1 text-sm text-gray-700" id="answer-type-description">The answser type is locked due to your show being publihsed. If there is a change that is needed please reach out to your show specialist.</p>

              </template>
            </div>
            <div id="pricing" class="col-span-4 space-y-2">
              <div class="relative flex items-center">
                <div class="flex items-center h-5">
                  <input v-model="entryQuestion.is_pricing" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
                </div>
                <div class="ml-3 text-sm">
                  <label for="comments" class="font-medium text-gray-700 text-base">Pricing Question</label>
                  <p id="comments-description" class="text-gray-600">Does this question have a price associated with it. Can be used for bedding, stalling, apparel, camping etc. **ANSWER TYPE CAN'T BE TEXT**</p>
                </div>
              </div>
              <div class="ml-4 px-6 bg-gray-200 p-4 rounded-lg mt-2" v-if="entryQuestion.is_pricing">
                <div class="w-full">
                  <label class="form-label">Base Price</label>
                  <input v-if="!isLocked" class="new-form-input w-full" type="number" v-model.number="entryQuestion.pricing_details.rate" placeholder="0">
                  <p v-else class="text-lg mt-1 font-medium cursor-not-allowed">{{currencySymbol}}{{ entryQuestion.pricing_details.rate }}</p>
                </div>
                <div v-if="this.event.entry_settings.has_member_pricing" class="w-full">
                  <label class="form-label">Base Member Price</label>
                  <input class="new-form-input w-full" type="number" v-model.number="entryQuestion.pricing_details.member_rate" placeholder="0">
                </div>
              </div>
            </div>
            <div v-if="entryQuestion.details.answer_type !== 'dropdown' && entryQuestion.details.answer_type !== undefined" class="col-span-4 ">
              <div class="relative flex items-center">
                <div class="flex items-center h-5">
                  <input :disabled="isLocked" v-model="entryQuestion.details.multi_answer" id="candidates" aria-describedby="candidates-description" name="candidates" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
                </div>
                <div class="ml-3 text-sm">
                  <label for="candidates" class="font-medium text-gray-700 text-base">Multiple Answer Options</label>
                  <p v-if="isLocked" class="font-semibold">This option is locked due to show being published. Please reach out to your show specialist if you need to make changes.</p>
                  <p v-else id="candidates-description" class="text-gray-600">This is a great option for T-Shirt or apparel questions where you have multipe size or for stalls/pens for multiple species or sizes.</p>
                </div>
              </div>
            </div>
            <template v-if="entryQuestion.details.answer_type === 'dropdown'">
              <div class="col-span-4 px-6 bg-gray-200 p-4 rounded-lg ml-4">
                <label class="form-label">Dropdown Options</label>
                <draggable :list="entryQuestion.details.dropdown_options_new" :options="{group:'fields'}" @start="drag=true" @end="drag=false" handle=".handle">
                  <div v-for="(option, i) in entryQuestion.details.dropdown_options_new" :key="i" class="flex items-center justify-start space-x-2 border-t pt-1 mt-2">
                    <div tabindex="0" role="button" aria-describedby="rbd-hidden-text-0-hidden-text-0" data-rbd-drag-handle-draggable-id="2oADuscO" data-rbd-drag-handle-context-id="0" draggable="false" class="~text-gray-500 flex h-9 w-6 items-center justify-center handle draggable"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-menu"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg></div>
                    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center sm:space-x-2">
                      <div>
                        <label for="Option" class="ml-1 block text-xs font-medium text-gray-700">Option</label>
                        <input :disabled="isQuestionOptionDisabled(entryQuestion)" v-model="option.value" class="w-56 mt-1" :class="{'new-form-input-disabled': isQuestionOptionDisabled(entryQuestion), 'new-form-input': !isQuestionOptionDisabled(entryQuestion)}">
                      </div>
                      <template v-if="entryQuestion.is_pricing && entryQuestion.details.answer_type !== 'text'">
                        <div>
                          <label for="rate" class="ml-1 block text-xs font-medium text-gray-700">Rate</label>
                          <input type="number" v-model.number="option.pricing_details.rate" class="mt-1 new-form-input w-20">
                        </div>
                        <div v-if="event.entry_settings.has_member_pricing">
                          <label for="member_rate" class="ml-1 block text-xs font-medium text-gray-700">Member Rate</label>
                          <input v-model="option.pricing_details.member_rate" class="mt-1 new-form-input w-20">
                        </div>
                      </template>
                    </div>
                    <div v-if="isQuestionOptionDisabled(entryQuestion)" class="relative flex items-center">
                      <div class="flex items-center h-5">
                        <input v-model="option.disabled" id="disabled" aria-describedby="disabled-description" name="comments" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
                      </div>
                      <div class="ml-3 text-sm">
                        <label for="comments" class="font-medium text-gray-700">Disabled</label>
                        <p id="comments-description" class="text-gray-600">Remove the ability for someone to select this option</p>
                      </div>
                    </div>
                    <button v-else @click="entryQuestion.details.dropdown_options_new.splice(i,1)" type="button" class="flex h-8 w-9 shrink-0 items-center justify-center text-red-600 hover:text-red-700">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-trash-2">
                        <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </draggable>
                <div class="mt-2 py-2 border-t">
                  <div class="flex items-center">
                    <div v-if="entryQuestion.details.dropdown_options_new.length" class="w-8"></div>
                    <div>
                      <input class="new-form-input w-56 mr-3" type="text" v-model="newOption" v-on:keyup.enter="addNewOption" placeholder="New Dropdown Option">
                    </div>
                    <button @click="addNewOption" type="button" class="text-blue-600 border-blue-600 border rounded px-2 py-1 flex items-center space-x-1 text-sm font-medium focus:outline-none focus:ring">
                      <div class="flex w-6 justify-center">
                        <div><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-plus"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></div>
                      </div>
                      <div>Add Option</div>
                    </button>
                  </div>
                  <p v-if="event.status === 'PUBLISHED'" class="mt-1 text-sm text-gray-700" id="answer-type-description">When the show is published you can't delete options. Just add new ones or disable existing options.</p>
                </div>
              </div>
            </template>
            <template v-else-if="entryQuestion.details.multi_answer">
              <div class="col-span-4 px-6 bg-gray-200 p-4 rounded-lg ml-4">
                <label class="form-label">Multi-Answer Options</label>
                <draggable :list="entryQuestion.details.answer_options" :options="{group:'fields'}" @start="drag=true" @end="drag=false" handle=".handle">
                  <div v-for="(option, i) in entryQuestion.details.answer_options" :key="i" class="flex items-center justify-start space-x-2 border-t pt-1 mt-2">
                    <div tabindex="0" role="button" aria-describedby="rbd-hidden-text-0-hidden-text-0" data-rbd-drag-handle-draggable-id="2oADuscO" data-rbd-drag-handle-context-id="0" draggable="false" class="~text-gray-500 flex h-9 w-6 items-center justify-center handle draggable"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-menu"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg></div>
                    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center sm:space-x-2">
                      <div>
                        <label for="Option" class="ml-1 block text-xs font-medium text-gray-700">Option</label>
                        <input :disabled="isQuestionOptionDisabled(entryQuestion)" v-model="option.value" class="w-56 mt-1" :class="{'new-form-input-disabled': isQuestionOptionDisabled(entryQuestion), 'new-form-input': !isQuestionOptionDisabled(entryQuestion)}">
                      </div>
                      <template v-if="entryQuestion.is_pricing && entryQuestion.details.answer_type !== 'text'">
                        <div>
                          <label for="rate" class="ml-1 block text-xs font-medium text-gray-700">Rate</label>
                          <input type="number" :disabled="isQuestionOptionDisabled(entryQuestion)" v-model.number="option.pricing_details.rate" class="mt-1 w-20" :class="{'new-form-input-disabled': isQuestionOptionDisabled(entryQuestion), 'new-form-input': !isQuestionOptionDisabled(entryQuestion)}">
                        </div>
                        <div v-if="event.entry_settings.has_member_pricing">
                          <label for="member_rate" class="ml-1 block text-xs font-medium text-gray-700">Member Rate</label>
                          <input type="number" :disabled="isQuestionOptionDisabled(entryQuestion)" v-model.number="option.pricing_details.member_rate" class="mt-1 w-20" :class="{'new-form-input-disabled': isQuestionOptionDisabled(entryQuestion), 'new-form-input': !isQuestionOptionDisabled(entryQuestion)}">
                        </div>
                      </template>
                    </div>
                    <div v-if="isQuestionOptionDisabled(entryQuestion)" class="relative flex items-center">
                      <div class="flex items-center h-5">
                        <input v-model="option.disabled" id="disabled" aria-describedby="disabled-description" name="comments" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
                      </div>
                      <div class="ml-3 text-sm">
                        <label for="comments" class="font-medium text-gray-700">Disabled</label>
                        <p id="comments-description" class="text-gray-500">Remove the ability for someone to select this option</p>
                      </div>
                    </div>
                    <button v-else @click="entryQuestion.details.answer_options.splice(i,1)" type="button" class="flex h-8 w-9 shrink-0 items-center justify-center text-red-600 hover:text-red-700">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-trash-2">
                        <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </draggable>
                <div class="mt-2 pt-2 border-t">
                  <div class="flex items-center">
                    <div v-if="entryQuestion.details.answer_options.length" class="w-8"></div>
                    <div>
                      <input class="new-form-input w-56 mr-3" type="text" v-on:keyup.enter="addNewAnswerOption" v-model="newOption" placeholder="New Answer Option">
                    </div>
                    <button @click="addNewAnswerOption" type="button" class="text-blue-600 border-blue-600 border rounded px-2 py-1 flex items-center space-x-1 text-sm font-medium focus:outline-none focus:ring">
                      <div class="flex w-6 justify-center">
                        <div><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-plus"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></div>
                      </div>
                      <div>Add Option</div>
                    </button>
                  </div>
                  <p v-if="event.status === 'PUBLISHED'" class="mt-1 text-sm text-gray-700" id="answer-type-description">When the show is published you can't delete options. Just add new ones or disable existing options.</p>
                </div>
              </div>
            </template>
          </div>

          <fieldset class="flex flex-col space-y-3 border-t border-t mt-4">
            <legend class="sr-only">Settings</legend>
            <div class="relative flex items-center">
              <div class="flex items-center h-5">
                <input v-model="entryQuestion.details.required" id="candidates" aria-describedby="candidates-description" name="candidates" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
              </div>
              <div class="ml-3 text-sm">
                <label for="candidates" class="font-medium text-gray-700">Required</label>
                <p id="candidates-description" class="text-gray-600">Requires exhibitors to answer this question before they can submit their entries.</p>
              </div>
            </div>
            <div class="relative flex items-center">
              <div class="flex items-center h-5">
                <input :disabled="isLocked" v-model="entryQuestion.details.exhibitor_question" id="offers" aria-describedby="offers-description" name="offers" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
              </div>
              <div class="ml-3 text-sm">
                <label for="offers" class="font-medium text-gray-700">Separate Answer Box for each exhibitor to answer?</label>
                <p v-if="isLocked" class="font-semibold">This option is locked due to show being published. Please reach out to your show specialist if you need to make changes.</p>
                <p v-else id="offers-description" class="text-gray-600">Don't check this box for questions that can be answered once for the group/family i.e. pens, shavings, camping etc.</p>
              </div>
            </div>
            <template v-if="isSuperUser">
              <template v-if="(entryQuestion.details.answer_type === 'number' || entryQuestion.details.answer_type === 'checkbox') && !entryQuestion.details.multi_answer && !entryQuestion.details.exhibitor_question">
                <div class="relative flex items-center" >
                  <div class="flex items-center h-5">
                    <input v-model="entryQuestion.details.limited_availability" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
                  </div>
                  <div class="ml-3 text-sm">
                    <label for="comments" class="font-medium text-gray-700">Limited Availability</label>
                    <p id="comments-description" class="text-gray-600">Is there a limited number of this item avaialble? **This is only available for basic number and checkbox answer types. Cannnot be used with Exhibitor questions or multi answer options.</p>
                  </div>
                </div>
                <div v-if="entryQuestion.details.limited_availability" class="px-6 bg-gray-200 p-4 rounded-lg ml-4">
                  <label class="form-label">Number Available</label>
                  <input v-model.number="entryQuestion.details.number_available" type="number" class="new-form-input mt-1 w-full">
                  <p class="text-xs pt-1 font-semibold text-gray-800">The entry system will keep track of # available as exhibtiors register or are added in.<br>**DO NOT manually change/update this number.</p>
                </div>
              </template>
            </template>
            <div class="relative flex items-center">
              <div class="flex items-center h-5">
                <input v-model="entryQuestion.details.admin_only" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
              </div>
              <div class="ml-3 text-sm">
                <label for="comments" class="font-medium text-gray-700">Admin Only</label>
                <p id="comments-description" class="text-gray-600">This question will only appear on the admin dashboard. Exhibitors won't be able to answer it.</p>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
      <button v-if="editType === 'new'" class="btn btn-blue" @click="addQuestion">Save Question</button>
      <button v-else class="btn btn-blue" @click="updateQuestion">Save Question</button>
      <div slot="modal-footer" class="w-100">
        <div class="flex justify-end">
          <button class="btn-red-outline btn" variant="primary" @click="$refs.entryQuestion.hide()">
            Cancel
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal ref="preview" title="Preview Entry Questions">
      <entry-questions  :event="event" :preview="true" :entryGroup="{entry_questions: {}}" :isAdmin="true"/>

    </b-modal>

  </div>
</template>

<script>
import draggable from 'vuedraggable'
import notie from 'notie'
import EntryQuestions from '@/components/EntryQuestions.vue'

export default {
  components: {
    draggable,
    EntryQuestions
  },
  props: {
    allEntryQuestions: Array
  },
  data () {
    return {
      entryQuestion: {
        details: {
          dropdown_options_new: [],
          limited_availability: false
        },
        pricing_details: {}
      },
      editType: null,
      answerTypeOptions: [
        {
          value: 'number',
          description: 'Provide a number box for exhibitors to input their answer.\nPricing questions are supported, amount on the line item will be the rate x the number submitted.'
        },
        {
          value: 'dropdown',
          description: 'Provide a select box for exhibitors to select their answer from a list of options.\nPricing questions are supported, amount on the line item will be whatever the rate is set to.'
        },
        {
          value: 'text',
          description: 'Provide a text field for exhibitors to type in their answer free form.\n**Pricing Questions are not supported for this answer type**'
        },
        {
          value: 'checkbox',
          description: 'Provide a checkbox for exhibitors to check if applicable.\nPricing questions are supported, amount on the line item will be whatever the rate is set to.'
        },
        {
          value: 'file',
          description: 'Provide a file upload field for exhibitors to upload a file.\n**Pricing Questions are not supported for this answer type**'
        }
      ],
      newOption: null,
      isUnlocked: false
    }
  },
  methods: {
    newEntryQuestionClicked () {
      this.entryQuestion = {
        details: {
          dropdown_options_new: [],
          answer_options: []
        },
        pricing_details: {},
        dropdown_options: []
      }
      this.editType = 'new'
      this.$refs.entryQuestion.show()
    },
    editEntryQuestionClicked (question) {
      this.entryQuestion = question
      if (!this.entryQuestion.details.dropdown_options_new) {
        this.entryQuestion.details.dropdown_options_new = []
      }
      if (!this.entryQuestion.details.answer_options) {
        this.entryQuestion.details.answer_options = []
      }

      this.editType = 'edit'
      this.$refs.entryQuestion.show()
    },
    addQuestion () {
      let errorMessage = this.checkQuestion(this.entryQuestion)
      if (errorMessage) {
        this.notie.alert({
          type: 2,
          text: errorMessage
        })
        return
      }
      this.$store.dispatch('createNewEntryQuestion', this.entryQuestion)
        .then(response => {
          this.entryQuestion = {
            details: {}
          }
          this.$refs.entryQuestion.hide()
        })
        .catch(err => {
          // console.log(err)
          alert(err)
        })
    },
    updateQuestion () {
      let errorMessage = this.checkQuestion(this.entryQuestion)
      if (errorMessage) {
        this.notie.alert({
          type: 2,
          text: errorMessage
        })
        return
      }
      if (!(this.entryQuestion.details.answer_type === 'number' || this.entryQuestion.details.answer_type === 'checkbox') || this.entryQuestion.details.multi_answer || this.entryQuestion.details.exhibitor_question) {
        this.entryQuestion.details.limited_availability = false
        this.entryQuestion.details.number_available = null // Reset the number available
      }
      this.$store.dispatch('updateEntryQuestion', this.entryQuestion)
        .then(response => {
          this.entryQuestion = {
            details: {}
          }
          this.$refs.entryQuestion.hide()
        })
        .catch(err => {
          // console.log(err)
          alert(err)
        })
    },
    checkQuestion (question) {
      let errorMessage = null
      // If pricing Questions check prices are set
      if (!question.details.label) {
        errorMessage = 'Please set a label for this question.'
      } else if (question.details.label.length > 25) {
        errorMessage = `Question label cannot be greater than 25 Characters. Currently your label is ${question.details.label.length} characters.`
      } else if (this.allEntryQuestions.filter(eq => eq.details.label === question.details.label && question.entry_question_id !== eq.entry_question_id).length > 0) {
        errorMessage = `Question labels must be unique, you currently have another question with this same label`
      } else if (!question.question) {
        errorMessage = 'Please specify the text for this question.'
      } else if (!question.details.due_date) {
        errorMessage = 'Please set a due date for this question.'
      } else if (!question.details.answer_type) {
        errorMessage = 'Please select an answer type for this question.'
      } else if (question.details.answer_type === 'dropdown') {
        // Make sure all options are unique and look for empty strings
        let options = []
        question.details.dropdown_options_new.forEach(option => {
          if (options.includes(option.value)) {
            errorMessage = 'Please make sure all dropdown options are unique.'
          }
          if (!option.value) {
            errorMessage = 'Please make sure all dropdown options have a value set.'
          }
          options.push(option.value)
        })
        if (question.is_pricing) {
          // Check that prices are set for all:
          question.details.dropdown_options_new.forEach(option => {
            // if (!option.pricing_details.rate) {
            if (isNaN(option.pricing_details.rate)) {
              errorMessage = 'Make sure pricing is set for all Dropdown Options'
            } else if (isNaN(option.pricing_details.member_rate) && this.event.entry_settings.has_member_pricing) {
              errorMessage = 'Make sure the Member pricing is set for all Dropdown Options'
            }
          })
        }
      } else if (question.is_pricing) {
        if (!question.pricing_details.rate) {
          errorMessage = 'Price is required for pricing questions. Please either set a price or uncheck the option for pricing question.'
        } else if (this.event.entry_settings.has_member_pricing && isNaN(question.pricing_details.member_rate)) {
          errorMessage = 'Please specify a member rate for this question. You can make it the same as the regular price if there is no discount for members.'
        } else if (question.details.answer_type === 'text') {
          errorMessage = 'Pricing questions are supported for answer types of TEXT. Please either remove the pricing option or change the answer type.'
        } else if (question.details.multi_answer) {
          question.details.answer_options.forEach(option => {
            // if (!option.pricing_details.rate) {
            if (isNaN(option.pricing_details.rate)) {
              errorMessage = 'Make sure pricing is set for all Dropdown Options'
            } else if (isNaN(option.pricing_details.member_rate) && this.event.entry_settings.has_member_pricing) {
              errorMessage = 'Make sure the Member pricing is set for all Dropdown Options'
            }
          })
        }
      }
      return errorMessage
    },
    removeEntryQuestion (question) {
      notie.confirm({
        text: 'Are you sure you want to delete this question? All data associated with this question will be removed from your show.',
        submitCallback: () => {
          this.$store.dispatch('removeEntryQuestion', question)
            .catch(err => {
              // console.log(err)
              alert(err)
            })
        }
      })
    },
    formatDate (d) {
      return this.Localized.formatDate(d, this.event.location_details.country)
    },
    saveOrder () {
      this.allEntryQuestions.forEach((s, i) => {
        if (s.order !== i + 1) {
          s.order = i + 1
          this.$store.dispatch('updateEntryQuestion', s)
        }
      })
    },
    addNewOption () {
      this.entryQuestion.details.dropdown_options_new.push({
        value: this.newOption,
        pricing_details: {
          member_rate: this.entryQuestion.pricing_details ? this.entryQuestion.pricing_details.member_rate : 0,
          rate: this.entryQuestion.pricing_details ? this.entryQuestion.pricing_details.rate : 0
        }
      })
      this.newOption = null
    },
    addNewAnswerOption () {
      this.entryQuestion.details.answer_options.push({
        value: this.newOption,
        pricing_details: {
          member_rate: this.entryQuestion.pricing_details ? this.entryQuestion.pricing_details.member_rate : 0,
          rate: this.entryQuestion.pricing_details ? this.entryQuestion.pricing_details.rate : 0
        },
        id: new Date().valueOf()
      })
      this.newOption = null
    },
    isQuestionOptionDisabled (q) {
      if (this.event.status === 'PUBLISHED' && q.entry_question_id) {
        return true
      } else {
        return false
      }
    }
  },
  computed: {
    event () {
      return this.$store.state.admin.adminData.event
    },
    isLocked () {
      if (this.entryQuestion.entry_question_id && this.isPublished && (!this.isSuperUser && !this.isUnlocked)) {
        return true
      } else {
        return false
      }
    },
    isPublished () {
      return this.event.status === 'PUBLISHED'
    },
    isSuperUser () {
      return this.currentUser.user_id === 'Clo1Va5v4pNuvE1TqHPNeKWFGay2'
    },
    currentUser () {
      return this.$store.state.user
    },
    isSupportUser () {
      return this.currentUser.role === 'ADMIN' || this.isSuperUser
    },
    currencySymbol () {
      return this.Localized.getCurrencyInfo(this.event.location_details.country).symbol
    }
  }
}
</script>

<style>
  .draggable:hover {
      cursor: grab;
  }
  .ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }
  .handle {}
</style>
